import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { universities } from '../../mocks/universities';

export default function OpportunitiesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const filteredUniversities = useMemo(() => {
    return universities.filter((university) => {
      const matchesSearch =
        university.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        university.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        university.departments?.some((dept) =>
          dept.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const matchesType = selectedType === 'all' || university.type === selectedType;
      const matchesRegion = selectedRegion === 'all' || university.region === selectedRegion;
      const matchesStatus = selectedStatus === 'all' || university.status === selectedStatus;

      return matchesSearch && matchesType && matchesRegion && matchesStatus;
    });
  }, [searchQuery, selectedType, selectedRegion, selectedStatus]);

  const getStatusBadge = (status: string) => {
    if (status === 'open') {
      return (
        <span className="inline-flex items-center gap-1 bg-primary-100 text-primary-700 px-3 py-1.5 rounded-full text-sm font-bold font-tajawal">
          <i className="ri-checkbox-circle-fill"></i>
          <span>مفتوح</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full text-sm font-bold font-tajawal">
        <i className="ri-time-line"></i>
        <span>قريباً</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-cairo">
            جميع الجامعات
          </h1>
          <p className="text-xl text-white/90 font-tajawal">
            استكشف {universities.length} جامعة في المملكة العربية السعودية
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 -mt-8 mb-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <i className="ri-search-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحث عن جامعة أو تخصص..."
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none font-tajawal text-sm"
                  style={{ paddingRight: '2.5rem' }}
                />
              </div>
            </div>

            {/* Type Filter */}
            <div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none font-tajawal text-sm cursor-pointer"
              >
                <option value="all">جميع الأنواع</option>
                <option value="government">حكومية</option>
                <option value="private">أهلية</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none font-tajawal text-sm cursor-pointer"
              >
                <option value="all">جميع الحالات</option>
                <option value="open">مفتوح</option>
                <option value="soon">قريباً</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="mb-6">
          <p className="text-gray-600 font-tajawal">
            عرض {filteredUniversities.length} من {universities.length} جامعة
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUniversities.map((university) => (
            <div
              key={university.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:scale-105 cursor-pointer group"
              onClick={() => navigate(`/opportunity/${university.id}`)}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={university.image}
                  alt={university.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4">{getStatusBadge(university.status)}</div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-gray-700 px-3 py-1.5 rounded-full text-xs font-bold font-tajawal">
                    {university.type === 'government' ? 'حكومية' : 'أهلية'}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 font-cairo group-hover:text-primary-600 transition-colors">
                  {university.name}
                </h3>
                <p className="text-gray-600 font-tajawal text-sm mb-4 line-clamp-2">
                  {university.description}
                </p>

                <div className="flex items-center gap-2 text-gray-500 text-sm mb-3 font-tajawal">
                  <i className="ri-map-pin-line"></i>
                  <span>{university.location}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-500 text-sm mb-4 font-tajawal">
                  <i className="ri-calendar-line"></i>
                  <span>التسجيل حتى: {university.deadline}</span>
                </div>

                {/* Available Departments */}
                {university.departments && university.departments.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {university.departments.slice(0, 3).map((dept, index) => (
                        <span
                          key={index}
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-tajawal"
                        >
                          {dept}
                        </span>
                      ))}
                      {university.departments.length > 3 && (
                        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-tajawal">
                          +{university.departments.length - 3} المزيد
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/opportunity/${university.id}`);
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-xl font-tajawal font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    عرض التفاصيل
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(university.registrationLink, '_blank');
                    }}
                    className="flex-1 bg-gray-900 text-white px-4 py-2.5 rounded-xl font-tajawal font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    سجل الآن
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredUniversities.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-2xl font-bold text-gray-900 mb-2 font-cairo">
              لم يتم العثور على نتائج
            </h3>
            <p className="text-gray-600 font-tajawal">
              جرب تغيير معايير البحث أو الفلاتر
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
